export type Mode = "dark" | "light";
export type Skin = "blue" | "pink" | "purple" | "orange" | "green";
export type MeetingStatus = "live" | "waiting" | "ended";
export const RECENT_MEETING_LIMIT = 5;

export type RadarMeeting = {
  id: string;
  room: string;
  participants: string[];
  participantIds?: string[];
  participantAvatarUrls?: Array<string | undefined>;
  startedAt: Date;
  status: MeetingStatus;
  endedAt?: Date;
};

export type RadarAvatarPerson = {
  id: string;
  name?: string;
  avatarUrl?: string;
};

export type RadarPerson = {
  id: string;
  name?: string;
  avatarUrl?: string;
  availability: string;
  timestamp: number;
};

export type UserSummary = {
  id: string;
  name?: string | null;
  email: string;
  image?: string;
};

export type WorkspaceRole = "admin" | "member";

export type IntegrationStatus = {
  configured: boolean;
  validationState: "valid" | "unconfigured";
  signingConfigured: boolean;
  webhookRegistered: boolean;
  validatedAt: number | null;
  lastSynchronizedAt: number | null;
  webhookDiagnostics?: {
    receivedCount: number;
    acceptedCount: number;
    rejectedCount: number;
    lastReceivedAt?: number;
    lastOutcome?: "accepted_changed" | "accepted_unchanged" | "signature_rejected" | "signature_repaired" | "invalid_json" | "invalid_payload";
    lastReason?: string;
    lastEventType?: string;
    lastEventOccurredAt?: number;
  };
};

export type IntegrationUpdate = {
  apiToken?: string;
  webhookSigningSecret?: string;
};

export type BootstrapState =
  | { status: "signed_out" }
  | { status: "unsupported_account"; user: UserSummary }
  | { status: "unconfigured"; user: UserSummary; organizationId: string; role: WorkspaceRole; integration: IntegrationStatus }
  | { status: "ready"; user: UserSummary; organizationId: string; role: WorkspaceRole; integration: IntegrationStatus };

export type RadarConnection = "connected" | "reconnecting" | "offline";
export type RadarViewState = "ready" | "loading" | "error" | "stale";
export type RadarRefreshState = "idle" | "loading" | "error";
export type SettingsViewState =
  | "empty"
  | "configured"
  | "saving"
  | "success"
  | "validation-error"
  | "server-error"
  | "unauthorized";

export type LegalPage = "privacy" | "terms";

export function legalPageForPath(pathname: string): LegalPage | null {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (normalized === "/privacy") return "privacy";
  if (normalized === "/terms") return "terms";
  return null;
}

export type AppFixture = {
  bootstrap: BootstrapState | null;
  meetings?: RadarMeeting[];
  people?: RadarPerson[];
  radarState?: RadarViewState;
  refreshState?: RadarRefreshState;
  connection?: RadarConnection;
  settingsOpen?: boolean;
  settingsState?: SettingsViewState;
  pushState?: import("./push").PushNotificationState;
  pushMessage?: string;
  mode?: Mode;
  skin?: Skin;
  badgeEnabled?: boolean;
  now?: Date;
  error?: string | null;
  buildGitCommit?: string;
};

export const MODES = {
  dark: {
    bg: "#0d0d0f", surface: "#161618", toolbar: "#1a1a1e", headingFrom: "#1a1a1e",
    headingTo: "#111113", text: "#f0f0f2", muted: "#6b6b7a", dim: "#3a3a45", readableMuted: "#9a9aa8", readableDim: "#858594",
    border: "#2a2a2e", toggle: "#000000", hover: "#1e1e22"
  },
  light: {
    bg: "#e8e8ee", surface: "#f5f5fa", toolbar: "transparent", headingFrom: "#f5f5fa",
    headingTo: "#ececf2", text: "#2b2b30", muted: "#6b6b7a", dim: "#bbbbc8", readableMuted: "#62626e", readableDim: "#71717d",
    border: "#e1e1e9", toggle: "#ebebf2", hover: "#ebebf0"
  }
} as const;

export const SKINS = {
  blue: { dot: "#799fe5", accent: "#799fe5", glow: "rgba(121,159,229,.18)", waiting: "#556fa0" },
  pink: { dot: "#ea65a1", accent: "#ea65a1", glow: "rgba(234,101,161,.18)", waiting: "#a44771" },
  purple: { dot: "#a78bfa", accent: "#a78bfa", glow: "rgba(167,139,250,.18)", waiting: "#7561af" },
  orange: { dot: "#e48a3c", accent: "#e48a3c", glow: "rgba(228,138,60,.18)", waiting: "#a0612a" },
  green: { dot: "#7cce1e", accent: "#7cce1e", glow: "rgba(124,206,30,.18)", waiting: "#579015" }
} as const;

export function artPeriod(date: Date): "morning" | "standard" | "afternoon" | "evening" {
  const hour = date.getHours();
  if (hour >= 5 && hour < 11) return "morning";
  if (hour >= 11 && hour < 14) return "standard";
  if (hour >= 14 && hour < 17) return "afternoon";
  return "evening";
}

export function formatDuration(start: Date, end: Date): string {
  const minutes = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60_000));
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

export function formatAgo(end: Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - end.getTime()) / 60_000));
  return minutes < 60 ? `${minutes}m ago` : `${Math.floor(minutes / 60)}h ago`;
}

export function recentEndedMeetings(
  meetings: RadarMeeting[],
  now: Date,
  limit = RECENT_MEETING_LIMIT
): RadarMeeting[] {
  return meetings
    .filter((meeting) => meeting.status === "ended" && meeting.endedAt &&
      meeting.endedAt.getFullYear() === now.getFullYear() &&
      meeting.endedAt.getMonth() === now.getMonth() &&
      meeting.endedAt.getDate() === now.getDate())
    .sort((a, b) => b.endedAt!.getTime() - a.endedAt!.getTime())
    .slice(0, limit);
}

export function meetingParticipants(meeting: RadarMeeting): RadarAvatarPerson[] {
  return meeting.participants.map((name, index) => ({
    id: meeting.participantIds?.[index] || `${meeting.id}-participant-${index}`,
    name,
    avatarUrl: meeting.participantAvatarUrls?.[index]
  }));
}

function normalizedAvailability(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, " ");
}

export function presenceSections(people: RadarPerson[], meetings: RadarMeeting[]): {
  available: RadarPerson[];
  notAvailable: RadarPerson[];
} {
  const activeMeetings = meetings.filter((meeting) => meeting.status === "live");
  const activeIds = new Set(activeMeetings.flatMap((meeting) => meeting.participantIds ?? []).filter(Boolean));
  const activeNamesWithoutIds = new Set(activeMeetings.flatMap((meeting) => {
    const ids = meeting.participantIds ?? [];
    return meeting.participants
      .filter((_, index) => !ids[index])
      .map((name) => name.trim().toLocaleLowerCase())
      .filter(Boolean);
  }));

  const seenIds = new Set<string>();
  const visible = people.filter((person) => {
    if (seenIds.has(person.id)) return false;
    seenIds.add(person.id);
    if (activeIds.has(person.id)) return false;
    return !person.name || !activeNamesWithoutIds.has(person.name.trim().toLocaleLowerCase());
  });

  return {
    available: visible.filter((person) => normalizedAvailability(person.availability) === "available"),
    notAvailable: visible.filter((person) => {
      const availability = normalizedAvailability(person.availability);
      return availability !== "available" && availability !== "offline";
    })
  };
}
